import {
	Box,
	Button,
	ButtonProps,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Icon,
	IconButton,
	type SxProps,
	type Theme
} from "@mui/material";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CancelButton, DeleteButton, SaveButton } from "~/components/Buttons";
import { MOBILE_NAV_MIN_TOUCH_TARGET_PX, PHONE_TOUCH_TARGET_SX } from "~/components/mobile/bottomNav/mobileBottomNav";
import RequiredIndicator from "~/components/RequiredIndicator";
import mergeSlotProps, { type SlotStyleProps } from "~/helpers/mergeSlotProps";
import useEnterSubmit from "~/hooks/useEnterSubmit";
import AppThemeContext from "~/providers/AppThemeContext";
import DockPanelContext from "~/providers/DockPanelContext";

export interface DialogActionButtons {
	children: React.ReactNode;
	props: ButtonProps;
}

export type DialogSize = "full" | "medium" | "small";

const HiddenWithDockPanelContext = createContext(false);

const DIALOG_SIZE_WIDTHS: Record<DialogSize, string> = {
	full: "1400px",
	medium: "900px",
	small: "500px"
};

const PHONE_CLOSE_BUTTON_SX = (theme: Theme) => ({
	[theme.breakpoints.down("md")]: {
		minHeight: MOBILE_NAV_MIN_TOUCH_TARGET_PX,
		minWidth: MOBILE_NAV_MIN_TOUCH_TARGET_PX,
		right: 4,
		top: 4
	}
});

const PHONE_FOOTER_BUTTONS_SX = (theme: Theme) => ({
	[theme.breakpoints.down("md")]: { "& .MuiButton-root": { minHeight: MOBILE_NAV_MIN_TOUCH_TARGET_PX } }
});

interface ModalDialogProps {
	actions?: DialogActionButtons[] | null;
	actionsDisabled?: boolean;
	/** Slot inserted after the first action button; use for non-Button actions (split buttons, ButtonGroups). */
	actionMiddle?: React.ReactNode;
	children?: React.ReactNode;
	close: () => void;
	closeButtonTourId?: string;
	/** While true the dialog can't be closed: pass it while a request the dialog started is running. */
	closeDisabled?: boolean;
	closeOnBackdropClick?: boolean;
	/** CSS pixel value for the dialog container max-width (e.g. "1400px"). Overrides `size`. Distinct from MUI's `maxWidth` prop which uses breakpoints. */
	dialogMaxWidth?: string;
	hidesOffDockPanel?: boolean;
	isHidden?: boolean;
	leftAction?: React.ReactNode;
	mergeSlotProps?: boolean;
	onDelete?: () => void;
	onSave?: () => void;
	onSubmit?: () => void;
	open: boolean;
	responsiveWidth?: boolean;
	saveButtonSx?: SxProps<Theme>;
	saveDisabled?: boolean;
	/** Label for the built-in `onSave` footer button; defaults to the translated "Save". */
	saveLabel?: string;
	showCloseButton?: boolean;
	showRequiredIndicator?: boolean;
	size?: DialogSize;
	title: string;
}
const ModalDialog: React.FC<ModalDialogProps & React.ComponentProps<typeof Dialog>> = ({
	actions,
	actionsDisabled = false,
	actionMiddle,
	children,
	close,
	closeButtonTourId,
	closeDisabled = false,
	closeOnBackdropClick = false,
	dialogMaxWidth,
	hidesOffDockPanel = false,
	isHidden = false,
	leftAction,
	mergeSlotProps: mergeSlotPropsEnabled = false,
	onDelete,
	onSave,
	onSubmit,
	open,
	responsiveWidth = false,
	saveButtonSx,
	saveDisabled = false,
	saveLabel,
	showCloseButton = true,
	showRequiredIndicator = false,
	size = "full",
	title,
	slotProps: slotPropsOverride,
	...otherProps
}) => {
	const { t } = useTranslation("common");
	const { themeType } = useContext(AppThemeContext);
	const isPanelInactive = useContext(DockPanelContext) === false;
	const isHiddenWithParent = useContext(HiddenWithDockPanelContext);
	const [isShownOverParent, setIsShownOverParent] = useState(true);
	// MUI stacks the modal that opens last on top, so a nested dialog hides and shows one render after the dialog under it.
	useEffect(() => setIsShownOverParent(!isHiddenWithParent), [isHiddenWithParent]);
	const isHiddenWithDockPanel = (hidesOffDockPanel && isPanelInactive) || !isShownOverParent;
	const isShown = open && !isHidden && !isHiddenWithDockPanel;
	const rootRef = useRef<HTMLDivElement>(null);
	const handleClose = (_event: React.SyntheticEvent, reason: "backdropClick" | "escapeKeyDown") => {
		if (closeDisabled) return;
		if (reason === "backdropClick" && !closeOnBackdropClick) return;
		close();
	};

	const submitHandler = onSubmit ?? onSave;
	useEnterSubmit(
		"modalDialog.submit",
		submitHandler || (() => {}),
		isShown && !!submitHandler && !actionsDisabled && !saveDisabled,
		rootRef
	);

	const baseContainer: SlotStyleProps = {
		sx: {
			maxWidth: otherProps.fullScreen ? "unset" : dialogMaxWidth || DIALOG_SIZE_WIDTHS[size],
			minWidth: otherProps.fullScreen ? "unset" : responsiveWidth ? { sm: "400px", xs: 0 } : "400px",
			width: otherProps.fullScreen ? "calc(100vw)" : "100%"
		}
	};
	const basePaper: SlotStyleProps = {
		sx: {
			margin: 2,
			maxHeight: "calc(100% - 32px)",
			maxWidth: "unset",
			overflow: "auto",
			width: "100%"
		}
	};
	const baseRoot: SlotStyleProps = {
		sx: {
			alignItems: "center",
			backgroundImage: "none",
			display: "flex",
			justifyContent: "center"
		}
	};

	const resolvedSlotProps = (
		mergeSlotPropsEnabled
			? {
					...slotPropsOverride,
					container: mergeSlotProps(baseContainer, slotPropsOverride?.container),
					paper: mergeSlotProps(basePaper, slotPropsOverride?.paper),
					root: mergeSlotProps(baseRoot, slotPropsOverride?.root)
				}
			: (slotPropsOverride ?? { container: baseContainer, paper: basePaper, root: baseRoot })
	) as React.ComponentProps<typeof Dialog>["slotProps"];

	const actionButtons = actions
		? actions.map(
				(action, i) =>
					action.props && (
						<Button
							key={i}
							variant="contained"
							{...action.props}
							sx={[PHONE_TOUCH_TARGET_SX, ...(Array.isArray(action.props.sx) ? action.props.sx : [action.props.sx])]}
						>
							{action.children}
						</Button>
					)
			)
		: null;

	return (
		<Dialog
			closeAfterTransition
			// disableEnforceFocus stays: MUI focus enforcement fights autocomplete poppers and AG Grid editors that portal outside the dialog DOM
			disableEnforceFocus={true}
			keepMounted={isHidden || (open && isHiddenWithDockPanel)}
			onClose={handleClose}
			open={isShown}
			ref={rootRef}
			slotProps={resolvedSlotProps}
			{...otherProps}
		>
			{title && (
				<DialogTitle sx={{ m: 0, pb: { sm: 2, xs: 1 }, pt: { sm: 2, xs: 1 }, px: { sm: 2, xs: 1 } }}>
					{title}
				</DialogTitle>
			)}
			{showCloseButton && (
				<IconButton
					aria-label={t("shell.modalDialog.closeAria")}
					data-tour-id={closeButtonTourId}
					disabled={closeDisabled}
					onClick={close}
					sx={[
						{
							color: (theme) => theme.palette.grey[500],
							position: "absolute",
							right: 8,
							top: 8
						},
						PHONE_CLOSE_BUTTON_SX
					]}
				>
					<Icon>close</Icon>
				</IconButton>
			)}
			{title && <Divider />}
			<DialogContent data-ag-theme-mode={themeType} sx={{ padding: { sm: 2, xs: 1 } }}>
				{showRequiredIndicator && (
					<Box sx={{ pb: 2 }}>
						<RequiredIndicator />
					</Box>
				)}
				<HiddenWithDockPanelContext.Provider value={isHiddenWithDockPanel}>
					<DockPanelContext.Provider value={undefined}>{children}</DockPanelContext.Provider>
				</HiddenWithDockPanelContext.Provider>
			</DialogContent>
			{onSave ? (
				<DialogActions
					sx={[
						{
							borderTop: (theme) => `1px solid ${theme.palette.divider}`,
							gap: 0.5,
							padding: (theme) => theme.spacing(1) + " " + theme.spacing(2),
							paddingRight: otherProps.fullScreen ? "60px" : undefined
						},
						PHONE_FOOTER_BUTTONS_SX
					]}
				>
					{onDelete && (
						<Box sx={{ mr: "auto" }}>
							<DeleteButton disabled={actionsDisabled} onClick={onDelete} />
						</Box>
					)}
					<CancelButton disabled={actionsDisabled || closeDisabled} onClick={close} />
					<SaveButton
						disabled={actionsDisabled || saveDisabled}
						fullWidth={false}
						onClick={onSave}
						{...(saveButtonSx ? { sx: saveButtonSx } : {})}
					>
						{saveLabel}
					</SaveButton>
				</DialogActions>
			) : actionButtons && actionButtons.length > 0 ? (
				<DialogActions
					sx={{
						borderTop: (theme) => `1px solid ${theme.palette.divider}`,
						padding: (theme) => theme.spacing(1) + " " + theme.spacing(2),
						paddingRight: otherProps.fullScreen ? "60px" : undefined
					}}
				>
					{leftAction ? (
						<Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", width: "100%" }}>
							<Box>{leftAction}</Box>
							<Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
								{actionButtons[0]}
								{actionMiddle}
								{actionButtons.slice(1)}
							</Box>
						</Box>
					) : (
						<Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
							{actionButtons[0]}
							{actionMiddle}
							{actionButtons.slice(1)}
						</Box>
					)}
				</DialogActions>
			) : null}
		</Dialog>
	);
};

export default ModalDialog;
